import type { GarageView } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { describeConflict } from '../policies/booking-labels';
import type { BookingTripConflict } from '../workshop-bookings.api';

const IN_HOUSE = 'in-house';

/** What the drawer is for: a new booking on a truck (optionally with a window to start from), or a booking whose window is being moved. */
export type BookingDrawerMode =
  | { kind: 'create'; initialWindow?: { startsAt: string; endsAt: string } }
  | { kind: 'move'; booking: { id: string; workshopName: string; startsAt: string; endsAt: string } };

export interface BookingFormValues {
  /** A garage id, or null for the in-house workshop. Moving keeps the workshop, so it is ignored there. */
  garageId: string | null;
  startsAt: Date;
  endsAt: Date;
  confirmConflicts: boolean;
}

interface BookingFormDrawerProps {
  mode: BookingDrawerMode | null;
  garages: GarageView[] | null;
  error: string | null;
  /** Committed trips the server refused the window for; the same submit then books anyway. */
  conflicts: BookingTripConflict[] | null;
  submitting: boolean;
  onSubmit: (values: BookingFormValues) => Promise<boolean>;
  onEdited: () => void;
  onClose: () => void;
}

interface FormFields {
  workshop: string;
  window: [Dayjs, Dayjs];
}

function initialFields(mode: BookingDrawerMode): Partial<FormFields> {
  if (mode.kind === 'move') return { window: [dayjs(mode.booking.startsAt), dayjs(mode.booking.endsAt)] };
  const initial = mode.initialWindow;
  return { workshop: IN_HOUSE, window: initial ? [dayjs(initial.startsAt), dayjs(initial.endsAt)] : undefined };
}

function drawerTitle(mode: BookingDrawerMode | null): string {
  return mode?.kind === 'move' ? `Move booking at ${mode.booking.workshopName}` : 'Book into a workshop';
}

function submitLabel(mode: BookingDrawerMode | null, conflicts: BookingTripConflict[] | null): string {
  if (conflicts) return 'Book anyway';
  return mode?.kind === 'move' ? 'Move booking' : 'Book';
}

function ConflictAlert({ conflicts }: { conflicts: BookingTripConflict[] }) {
  return (
    <Alert
      className="mb-5"
      type="warning"
      showIcon
      title="This window runs into a committed trip"
      description={
        <ul className="m-0 pl-4">
          {conflicts.map((conflict) => (
            <li key={conflict.tripId}>{describeConflict(conflict)}</li>
          ))}
        </ul>
      }
    />
  );
}

function WorkshopField({ garages }: { garages: GarageView[] | null }) {
  const options = [{ value: IN_HOUSE, label: 'In-house workshop' }, ...(garages ?? []).map((garage) => ({ value: garage.id, label: garage.name }))];
  return (
    <Form.Item name="workshop" label="Workshop" rules={[{ required: true, message: 'Choose where the truck goes' }]}>
      <Select showSearch loading={garages === null} optionFilterProp="label" placeholder="Select or search a workshop" options={options} />
    </Form.Item>
  );
}

/**
 * Books a truck into a workshop, or moves a booking to another window. A window that runs into a committed trip is not refused outright:
 * the trips are listed and the button becomes "Book anyway", and the server keeps which trips were confirmed. A short form, so a drawer.
 */
export function BookingFormDrawer({ mode, garages, error, conflicts, submitting, onSubmit, onEdited, onClose }: BookingFormDrawerProps) {
  const [form] = Form.useForm<FormFields>();
  const isMove = mode?.kind === 'move';

  useEffect(() => {
    if (!mode) return;
    form.resetFields();
    form.setFieldsValue(initialFields(mode));
  }, [mode, form]);

  async function submit(fields: FormFields) {
    const [start, end] = fields.window;
    const garageId = isMove || fields.workshop === IN_HOUSE ? null : fields.workshop;
    if (await onSubmit({ garageId, startsAt: start.toDate(), endsAt: end.toDate(), confirmConflicts: conflicts !== null })) onClose();
  }

  const footer = (
    <div className="flex justify-end gap-2">
      <Button onClick={onClose}>Close</Button>
      <Button type="primary" loading={submitting} onClick={() => form.submit()}>
        {submitLabel(mode, conflicts)}
      </Button>
    </div>
  );

  return (
    <Drawer open={mode !== null} onClose={onClose} destroyOnHidden size={440} title={drawerTitle(mode)} footer={footer}>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      {conflicts && <ConflictAlert conflicts={conflicts} />}
      <Form<FormFields> form={form} layout="vertical" requiredMark={false} onFinish={submit} onValuesChange={onEdited} disabled={submitting}>
        {!isMove && <WorkshopField garages={garages} />}
        <Form.Item name="window" label="From and to" rules={[{ required: true, message: 'Choose when the truck is in the workshop' }]}>
          <DatePicker.RangePicker className="w-full" showTime={{ format: 'HH:mm', minuteStep: 15 }} format="DD MMM YYYY HH:mm" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
